import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { List } from 'immutable';
import { Modal } from '@els/els-react--modal';
import { Icon } from '@els/els-react--icon';
import { ButtonWithIcon } from '@els/els-react--button';
import Footer from '@Components/Footer';
import useDeleteFocusManager from '@Hooks/useDeleteFocusManager';
import { LEGACY_DIALOG_SIZE } from '@Containers/Dialogs/DialogsConstants';

import NoAlerts from './AlertsComponents/NoAlerts';
import GroupsList from './AlertsComponents/GroupsList';

import './Alerts.scss';

const FALLBACK_FOCUS_ID = 'alerts-icon'; // Alerts icon in the header, used as a fallback focus target when the deleted item is not in the list anymore (e.g. when deleting the last alert in a group)

function buildAlertDeleteButtonId(id) {
  return `alert-delete-${id}`;
}

export default function Alerts(props) {
  const {
    groupsIds,
    alertsIds: alertsIdsList,
    showDeleteModal,
    alertIdToDelete,
    handleViewDeleteModal,
    removeAlert,
    isDeleteInProgress,
    deletingAlertId,
  } = props;
  const result = groupsIds && (groupsIds.count() ? <GroupsList groupsIds={groupsIds} /> : <NoAlerts />);

  const deleteButtonIds = useMemo(() => {
    if (!alertsIdsList) return [];

    const alertIds = alertsIdsList.toJS();

    return alertIds.map(buildAlertDeleteButtonId);
  }, [alertsIdsList]);

  const deletingId = deletingAlertId ? buildAlertDeleteButtonId(deletingAlertId) : null;

  useDeleteFocusManager({
    itemIds: deleteButtonIds,
    deletingId,
    isDeleting: !!isDeleteInProgress,
    fallbackFocusId: FALLBACK_FOCUS_ID,
  });

  return (
    <div className="alerts-page__container alerts-container">
      {showDeleteModal && (
        <Modal
          id="alerts-delete-confirmation-dialog"
          type="secondary"
          width={LEGACY_DIALOG_SIZE}
          onClose={() => handleViewDeleteModal(false)}>
          <div>
            <header className="o-els-flex-layout o-els-flex-layout--left o-els-flex-layout--middle">
              <Icon sprite="AlertSolidSquare" size="m" color="secondary" isVisible isDecorative />
              <h4 id="alerts-confirm-dialog-title" className="u-els-margin-left">
                Delete
              </h4>
            </header>
            <section className="u-els-margin-top-1o2 u-els-margin-bottom u-els-margin-left-3x">
              Are you sure you want to delete this alert?
            </section>
            <footer className="o-els-flex-layout o-els-flex-layout--right o-els-flex-layout--middle">
              <ButtonWithIcon
                data-e2e="alert-delete-cancel-btn"
                type="secondary"
                sprite="Close"
                size="small"
                iconRight
                name="cancel"
                onClick={() => handleViewDeleteModal(false)}>
                Cancel
              </ButtonWithIcon>
              <ButtonWithIcon
                data-e2e="alert-delete-confirm-btn"
                type="primary"
                sprite="ChevronRight"
                size="small"
                iconRight
                className="u-els-margin-left"
                name="confirm"
                onClick={() => {
                  removeAlert(alertIdToDelete);
                  handleViewDeleteModal(false);
                }}>
                Yes
              </ButtonWithIcon>
            </footer>
          </div>
        </Modal>
      )}
      <div id="main-content" className="alerts-content" aria-labelledby="alerts-title">
        <section className="rx-list-wrapper">
          <div className="header-container--border-bottom u-els-padding-top-1x u-els-padding-bottom-1x">
            <h1 id="alerts-title" tabIndex="-1" className="alerts-heading u-els-font-size-h3 rx-left-gutter">
              Alerts
            </h1>
          </div>
          <div className="alerts-items-wrapper rx-list-wrapper rx-left-gutter rx-right-gutter">{result}</div>
        </section>
      </div>
      <Footer />
    </div>
  );
}

Alerts.propTypes = {
  groupsIds: PropTypes.instanceOf(List),
  alertsIds: PropTypes.instanceOf(List),
  showDeleteModal: PropTypes.bool,
  alertIdToDelete: PropTypes.string,
  handleViewDeleteModal: PropTypes.func,
  removeAlert: PropTypes.func,
  isDeleteInProgress: PropTypes.bool,
  deletingAlertId: PropTypes.string,
};
